import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";

import Header from "./components/Header";
import Footer from "./components/Footer";

import { BrowserRouter, MemoryRouter } from "react-router-dom";

import "./index.css";

const baseUrl = import.meta.env.BASE_URL || "/";

const normalizedBasename =
  baseUrl === "/" ? "/" : baseUrl.replace(/\/+$/, "");

const LEGACY_DEPLOYMENT_BASE = "/wp-content/uploads/test";

function stripKnownBase(pathname) {
  const value = String(pathname || "/");
  if (value === LEGACY_DEPLOYMENT_BASE) return "/";
  if (value.startsWith(`${LEGACY_DEPLOYMENT_BASE}/`)) {
    return value.slice(LEGACY_DEPLOYMENT_BASE.length) || "/";
  }

  if (normalizedBasename !== "/" && value.startsWith(normalizedBasename)) {
    return value.slice(normalizedBasename.length) || "/";
  }

  return value;
}

function resolveRuntimeBasename(pathname) {
  if (!pathname || typeof pathname !== "string") return normalizedBasename;
  if (pathname === LEGACY_DEPLOYMENT_BASE || pathname.startsWith(`${LEGACY_DEPLOYMENT_BASE}/`)) {
    return LEGACY_DEPLOYMENT_BASE;
  }
  if (normalizedBasename === "/") return "/";
  return pathname.startsWith(normalizedBasename) ? normalizedBasename : "/";
}

function normalizeAppPathname(pathname) {
  if (!pathname || typeof pathname !== "string") return "/";

  let nextPathname = stripKnownBase(pathname);

  nextPathname = nextPathname.replace(/\/+(?:index|home)\.php$/i, "/");
  nextPathname = nextPathname.replace(/\/index\.html$/i, "/");
  nextPathname = nextPathname.replace(/\.php$/i, "");

  if (!nextPathname.startsWith("/")) {
    nextPathname = `/${nextPathname}`;
  }

  return nextPathname === "" ? "/" : nextPathname;
}

const BUILD_ID =
  typeof __TRW_BUILD_ID__ !== "undefined" ? __TRW_BUILD_ID__ : null;

const checkForDeployedUpdate = () => {
  if (!BUILD_ID || typeof window === "undefined" || !window.fetch) return;

  const versionUrl = `${baseUrl}version.json?ts=${Date.now()}`;
  window
    .fetch(versionUrl, { cache: "no-store" })
    .then((res) => (res.ok ? res.json() : null))
    .then((payload) => {
      const deployedBuildId = payload?.buildId ? String(payload.buildId) : "";
      if (!deployedBuildId || deployedBuildId === BUILD_ID) return;

      const url = new URL(window.location.href);
      url.searchParams.set("v", deployedBuildId);
      window.location.replace(url.toString());
    })
    .catch(() => {});
};

const embeddedRoute =
  typeof window !== "undefined" &&
  typeof window.__TRW_EMBED_ROUTE__ === "string" &&
  window.__TRW_EMBED_ROUTE__.trim()
    ? window.__TRW_EMBED_ROUTE__.trim()
    : null;

if (typeof window !== "undefined") {
  const currentPathname = window.location.pathname || "/";
  const runtimeBasename = resolveRuntimeBasename(currentPathname);
  const normalizedPathname = normalizeAppPathname(currentPathname);
  if (runtimeBasename !== "/" && normalizedPathname !== currentPathname) {
    const nextUrl = new URL(window.location.href);
    nextUrl.pathname = `${runtimeBasename}${normalizedPathname}`.replace(/\/+/g, "/");
    window.history.replaceState(window.history.state, "", nextUrl.toString());
  }
}

const runtimeBasename =
  typeof window !== "undefined"
    ? resolveRuntimeBasename(window.location.pathname || "/")
    : normalizedBasename;

const rootEl = document.getElementById("root");
const headerEl = document.getElementById("trw-header-placeholder");
const footerEl = document.getElementById("trw-footer-placeholder");

if (headerEl) {
  ReactDOM.createRoot(headerEl).render(<Header />);
}

if (footerEl) {
  ReactDOM.createRoot(footerEl).render(<Footer />);
}

if (rootEl) {
  ReactDOM.createRoot(rootEl).render(
    <HelmetProvider>
      {embeddedRoute ? (
        <MemoryRouter initialEntries={[embeddedRoute]}>
          <App />
        </MemoryRouter>
      ) : (
        <BrowserRouter basename={runtimeBasename}>
          <App />
        </BrowserRouter>
      )}
    </HelmetProvider>
  );
}

// Defer update checks so they don't compete with initial load/LCP requests.
if (typeof window !== "undefined") {
  const schedule =
    window.requestIdleCallback ||
    ((cb) => window.setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 2500));
  schedule(() => checkForDeployedUpdate());
}
